import { API_BASE_URL } from './api';
import { authService } from './authService';

const authHeaders = (): Record<string, string> => ({ ...authService.getAuthHeaders() } as Record<string, string>);
const authJsonHeaders = (): Record<string, string> => ({ 'Content-Type': 'application/json', ...authHeaders() });

/** The story a message was sent from — a snapshot, since stories expire and are deleted within a day. */
export interface StoryRef {
  storyId: string;
  authorUserId: string;
  mediaKey: string;
  caption?: string | null;
  /** Set for a one-tap reaction; the message text is then the emoji itself. */
  reaction?: string | null;
}

export interface DmMessage {
  conversationId: string;
  messageId: string;
  senderUserId: string;
  text: string;
  createdAt: string;
  /** Present when this message replied to (or reacted to) a story. */
  story?: StoryRef | null;
}

export interface DmConversationSummary {
  conversationId: string;
  otherUserId: string;
  otherDisplayName: string;
  otherProfilePhotoUrl?: string | null;
  lastMessagePreview?: string | null;
  lastMessageSenderUserId?: string | null;
  updatedAt: string;
  hasUnread: boolean;
}

export interface DmThread {
  conversationId: string;
  messages: DmMessage[];
}

const errorFrom = async (res: Response, fallback: string): Promise<string> => {
  try {
    const body = await res.text();
    return body || fallback;
  } catch {
    return fallback;
  }
};

// This app has no realtime transport (no WebSockets/SignalR) — the inbox and unread count
// are meant to be polled, same 30-second cadence useNotifications already uses, not a live
// chat feel.
export const messageService = {
  getInbox: async (): Promise<DmConversationSummary[]> => {
    const res = await fetch(`${API_BASE_URL}/messages`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load messages'));
    return res.json();
  },

  getUnreadCount: async (): Promise<number> => {
    const res = await fetch(`${API_BASE_URL}/messages/unread-count`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load unread count'));
    const body = await res.json();
    return body.unreadCount;
  },

  getThread: async (otherUserId: string): Promise<DmThread> => {
    const res = await fetch(`${API_BASE_URL}/messages/${encodeURIComponent(otherUserId)}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load conversation'));
    return res.json();
  },

  sendMessage: async (otherUserId: string, text: string): Promise<DmMessage> => {
    const res = await fetch(`${API_BASE_URL}/messages/${encodeURIComponent(otherUserId)}`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to send message'));
    return res.json();
  },
};
